import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';
import { fetchManifest, fetchPackages, fetchPrecheck, generatePackage } from './api';
import type { AuditPackageDto, PackagePrecheckDto } from './types';

const KEY = 'audit-package' as const;

export const usePrecheck = (batchId: string | undefined) => {
  return useQuery<PackagePrecheckDto, ApiClientError>({
    queryKey: [KEY, 'precheck', batchId],
    queryFn: () => fetchPrecheck(batchId!),
    enabled: !!batchId,
  });
};

export const usePackages = (batchId: string | undefined) => {
  return useQuery<AuditPackageDto[], ApiClientError>({
    queryKey: [KEY, 'list', batchId],
    queryFn: () => fetchPackages(batchId!),
    enabled: !!batchId,
  });
};

export const useGeneratePackage = () => {
  const qc = useQueryClient();
  return useMutation<AuditPackageDto, ApiClientError, string>({
    mutationFn: (batchId) => generatePackage(batchId),
    onSuccess: (_, batchId) => {
      qc.invalidateQueries({ queryKey: [KEY, 'precheck', batchId] });
      qc.invalidateQueries({ queryKey: [KEY, 'list', batchId] });
    },
  });
};

export const useManifest = (auditPackageId: string | undefined) => {
  return useQuery<Record<string, unknown>, ApiClientError>({
    queryKey: [KEY, 'manifest', auditPackageId],
    queryFn: () => fetchManifest(auditPackageId!),
    enabled: !!auditPackageId,
  });
};
